import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card, Form, Input, Select, Switch, DatePicker, Space, Spin, Tabs, Divider, message } from 'antd'
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import Editor from '@monaco-editor/react'
import blogService from '../services/blogService'
import dayjs from 'dayjs'

import { adminPath } from '../../shared/config/paths';

const MarkdownEditor = ({ value, onChange }) => (
  <Editor
    height="500px"
    defaultLanguage="markdown"
    theme="vs-dark"
    value={value || ''}
    onChange={(val) => onChange?.(val)}
    options={{
      minimap: { enabled: false },
      wordWrap: 'on',
      lineNumbers: 'on',
      fontSize: 14,
      scrollBeyondLastLine: false
    }}
  />
)

const locales = [
  { code: 'en', label: 'English' },
  { code: 'zh-CN', label: '简体中文' },
  { code: 'zh-TW', label: '繁體中文' },
  { code: 'ms', label: 'Bahasa Melayu' }
]

const defaultTranslations = () =>
  locales.reduce((map, locale) => {
    map[locale.code] = { title: '', summary: '', content: '' }
    return map
  }, {})

const BlogPostEditor = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [categories, setCategories] = useState([])
  const [tags, setTags] = useState([])
  const isEditing = !!id

  const tagOptions = useMemo(
    () =>
      tags.map((tag) => ({
        label: tag.translations?.['zh-CN']?.name || tag.translations?.en?.name || tag.slug,
        value: String(tag.id),
        keywords: [tag.slug, tag.translations?.['zh-CN']?.name, tag.translations?.en?.name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
      })),
    [tags]
  )

  useEffect(() => {
    loadMeta()
    if (isEditing) {
      loadPost()
    } else {
      form.setFieldsValue({
        status: 'draft',
        featured: false,
        pinned: false,
        translations: defaultTranslations()
      })
    }
  }, [id])

  const loadMeta = async () => {
    try {
      const [cats, tagList] = await Promise.all([blogService.getCategories(), blogService.getTags()])
      setCategories(cats.data || cats || [])
      setTags(tagList.data || tagList || [])
    } catch (error) {
      console.error('加载分类/标签失败', error)
    }
  }

  const loadPost = async () => {
    setLoading(true)
    try {
      const response = await blogService.getPost(id)
      const detail = response.data || response.post || response
      const translationMap = defaultTranslations()
      detail.translations?.forEach((item) => {
        translationMap[item.locale] = {
          title: item.title,
          summary: item.summary,
          content: item.content,
          seo_title: item.seo_title,
          seo_description: item.seo_description,
          seo_keywords: item.seo_keywords
        }
      })
      form.setFieldsValue({
        ...detail,
        category_ids: detail.categories?.map((cat) => cat.id) || [],
        tag_ids: detail.tags?.map((tag) => String(tag.id)) || [],
        translations: translationMap,
        published_at: detail.published_at ? dayjs(detail.published_at) : null
      })
    } catch (error) {
      message.error('获取文章详情失败')
      navigate(adminPath('/blog'))
    } finally {
      setLoading(false)
    }
  }

  const findTagMatch = (value) => {
    if (!value) return null
    const numeric = Number(value)
    if (!Number.isNaN(numeric)) {
      return tags.find((tag) => Number(tag.id) === numeric) || null
    }
    return (
      tags.find(
        (tag) =>
          tag.slug === value ||
          tag.translations?.['zh-CN']?.name === value ||
          tag.translations?.en?.name === value
      ) || null
    )
  }

  const buildTagTranslations = (name) =>
    locales.reduce((map, locale) => {
      map[locale.code] = { name }
      return map
    }, {})

  const uniqueNumericIds = (list) => {
    const seen = new Set()
    const output = []
    list.forEach((value) => {
      const numeric = Number(value)
      if (Number.isNaN(numeric)) return
      if (!seen.has(numeric)) {
        seen.add(numeric)
        output.push(numeric)
      }
    })
    return output
  }

  const ensureTags = async (selected = []) => {
    if (!selected?.length) return []
    const resolvedIds = []
    const pendingCreates = []

    selected.forEach((raw) => {
      const existing = findTagMatch(raw)
      if (existing) {
        resolvedIds.push(existing.id)
        return
      }
      const numeric = Number(raw)
      if (!Number.isNaN(numeric) && tags.find((tag) => Number(tag.id) === numeric)) {
        resolvedIds.push(numeric)
        return
      }
      const trimmed = String(raw).trim()
      if (trimmed) pendingCreates.push(trimmed)
    })

    if (pendingCreates.length === 0) return uniqueNumericIds(resolvedIds)

    const newlyCreated = []
    for (const name of pendingCreates) {
      try {
        const response = await blogService.createTag({
          status: 'active',
          translations: buildTagTranslations(name)
        })
        const created = response.data || response.tag || response
        if (created?.id) {
          resolvedIds.push(created.id)
          newlyCreated.push(created)
        }
      } catch (error) {
        message.error(`创建标签 ${name} 失败`)
      }
    }

    if (newlyCreated.length) setTags((prev) => [...prev, ...newlyCreated])
    return uniqueNumericIds(resolvedIds)
  }

  const handleSubmit = async () => {
    try {
      setSaving(true)
      const values = await form.validateFields()
      const resolvedTagIds = await ensureTags(values.tag_ids || [])

      const payload = {
        ...values,
        id: isEditing ? Number(id) : undefined,
        category_ids: values.category_ids || [],
        tag_ids: resolvedTagIds,
        translations: values.translations || {},
        published_at: values.published_at ? values.published_at.toISOString() : null
      }

      if (isEditing) {
        await blogService.updatePost(payload)
        message.success('文章已更新')
      } else {
        await blogService.createPost(payload)
        message.success('文章已创建')
      }
      navigate(adminPath('/blog'))
    } catch (error) {
      if (error?.errorFields) return
      message.error(error?.response?.data?.error || error?.message || '保存失败')
    } finally {
      setSaving(false)
    }
  }

  const handleBack = () => {
    navigate(adminPath('/blog'))
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <Card
      title={
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            返回
          </Button>
          <span>{isEditing ? '编辑文章' : '新建文章'}</span>
        </Space>
      }
      extra={
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSubmit}>
          保存
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <Form.Item label="Slug" name="slug" extra="不填写将自动生成">
            <Input placeholder="cloud-edge-routing" />
          </Form.Item>
          <Form.Item label="状态" name="status" initialValue="draft">
            <Select
              options={[
                { value: 'draft', label: '草稿' },
                { value: 'scheduled', label: '定时' },
                { value: 'published', label: '已发布' }
              ]}
            />
          </Form.Item>
          <Form.Item label="发布时间" name="published_at">
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr', gap: 16 }}>
          <Form.Item label="精选" name="featured" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="置顶" name="pinned" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="阅读时长（分钟）" name="reading_time" initialValue={4}>
            <Input type="number" min={1} />
          </Form.Item>
          <Form.Item label="封面图 URL" name="hero_image">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item label="列表缩略图 URL" name="thumbnail">
            <Input placeholder="https://..." />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Form.Item label="分类" name="category_ids">
            <Select
              mode="multiple"
              options={categories.map((cat) => ({
                label: cat.translations?.['zh-CN']?.name || cat.slug,
                value: cat.id
              }))}
            />
          </Form.Item>
          <Form.Item label="标签" name="tag_ids">
            <Select
              showSearch
              mode="tags"
              tokenSeparators={[',', '，']}
              options={tagOptions}
              filterOption={(input, option) => {
                if (!option) return false
                const needle = input.trim().toLowerCase()
                if (!needle) return true
                const base = [option.label ?? '', option.keywords ?? '', option.value ?? '']
                  .join(' ')
                  .toLowerCase()
                return base.includes(needle)
              }}
            />
          </Form.Item>
        </div>

        <Divider />

        <Tabs>
          {locales.map((localeItem) => (
            <Tabs.TabPane tab={localeItem.label} key={localeItem.code}>
              <Form.Item
                label="标题"
                name={['translations', localeItem.code, 'title']}
                rules={[{ required: localeItem.code === 'zh-CN', message: '请输入标题' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item label="摘要" name={['translations', localeItem.code, 'summary']}>
                <Input.TextArea rows={2} />
              </Form.Item>
              <Form.Item label="正文" name={['translations', localeItem.code, 'content']}>
                <MarkdownEditor />
              </Form.Item>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <Form.Item label="SEO 标题" name={['translations', localeItem.code, 'seo_title']}>
                  <Input />
                </Form.Item>
                <Form.Item label="SEO 描述" name={['translations', localeItem.code, 'seo_description']}>
                  <Input />
                </Form.Item>
                <Form.Item label="SEO 关键词" name={['translations', localeItem.code, 'seo_keywords']}>
                  <Input />
                </Form.Item>
              </div>
            </Tabs.TabPane>
          ))}
        </Tabs>
      </Form>
    </Card>
  )
}

export default BlogPostEditor
